import _ from 'lodash';
import { createSelector } from 'reselect';

import {
  getAddedFormModelElements,
  getComponentProps,
  getDataSources,
  getDeletedFormModelElements,
  getDeltaChanges,
  getEditor,
  getEntityDefinition,
  getEntityTranslations,
  getFormModel,
  getOriginalDataSources,
  getOriginalFormModel,
  getSessionFormModel,
  getTranslations,
} from './common.selectors';
import { getModelById, getOriginalModelById } from './form-manager.selectors';
import {
  ATTACHMENT_TYPES,
  DATALOOKUP_TYPE_EXTERNAL_API,
  SS_CONTROL_TYPE,
} from '../constants/form-manager.constants';
import { INTERACTION_RULES_SUPPORTED_TYPES } from '../constants/runtime.constants';
import {
  getControlModelWithAllUpdateRules,
  getTableData,
  getUniqueIdentifierData,
} from '../utils/actions/rule.actions.utils';
import {
  difference,
  getFlattenedEntityFields,
  preProcessActions,
  preProcessRules,
} from '../utils/editor.utils';
import { findClosestParentIdByType } from '../utils/form-manager.utils';

const getApiConfigurations = (state, props) =>
  state.formDesigner[props?.formKey]?.apiConfigurations ?? [];

export const getSelectedControl = createSelector(
  [getEditor],
  (editor) => editor?.selectedControl ?? {}
);

export const getSelectedControlId = createSelector(
  [getSelectedControl],
  (selectedControl) => selectedControl.selectedControlId
);

export const getSelectedControlProperty = createSelector(
  [getSelectedControlId, getFormModel, getComponentProps],
  (selectedControlId, formModel, { prop }) => {
    const control = formModel[selectedControlId];
    if (control && prop) {
      return control[prop];
    }
    return control;
  }
);

export const getSelectedDataSource = createSelector(
  [getSelectedControlProperty, getDataSources],
  (selectedControlProperty, dataSourceList) => {
    return typeof selectedControlProperty === 'string'
      ? dataSourceList[selectedControlProperty]
      : {};
  }
);

export const getSelectedDataSourceFromModel = createSelector(
  [getDataSources, getComponentProps],
  (dataSourceList, { dataSourceFromStore }) => {
    return typeof dataSourceFromStore === 'string' ? dataSourceList[dataSourceFromStore] : {};
  }
);

export const isSelectedControl = createSelector(
  [getEditor, getComponentProps],
  (editor, { id }) => editor.selectedControl?.selectedControlId === id
);

export const getShowControlWrapper = createSelector(
  [getSelectedControl, getComponentProps],
  (selectedControl, { id }) => {
    if (selectedControl?.selectedControlId === id) {
      return selectedControl.showControlWrapper;
    }
    return false;
  }
);

export const getPreProcessedRules = createSelector([getFormModel], (formModel) =>
  preProcessRules(formModel)
);

export const getDeltaChangesByControl = createSelector(
  [getDeltaChanges, getComponentProps],
  (deltaChanges, { id, name }) => {
    return deltaChanges?.controls?.length > 0
      ? deltaChanges?.controls
          .filter((control) =>
            control.ControlId.includes('formBox_')
              ? id.includes(control.ControlId)
              : control.ControlId === id
          )
          .filter((control) => {
            return control.Properties.some((property) => {
              return _.lowerFirst(property.name) === name;
            });
          })
      : [];
  }
);

export const getDeltaChangesUpdateRules = createSelector(
  [getDeltaChanges, getComponentProps],
  (deltaChanges, { id }) => {
    const changedControl = deltaChanges?.controls?.find((control) =>
      control.ControlId.includes('formBox_')
        ? id.includes(control.ControlId)
        : control.ControlId === id
    );

    if (!changedControl) {
      return false;
    }

    const updateRulesChanges = changedControl.Properties?.find(
      (property) => property.name === 'update'
    );

    if (updateRulesChanges) {
      return true;
    }

    const interactionRulesChanges = changedControl.Properties?.find(
      (property) => property.name === 'interaction'
    );

    return !!interactionRulesChanges?.currentValue?.rules?.filter((rule) =>
      rule.actions?.some((action) => INTERACTION_RULES_SUPPORTED_TYPES.includes(action.type))
    );
  }
);

export const getClosestParentIdByType = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { id, type }) => findClosestParentIdByType(formModel, id, type)
);

export const getOriginalModel = createSelector(
  [getOriginalFormModel, getComponentProps],
  (originalFormModel, { id }) => originalFormModel[id]
);

export const getOriginalPropByModel = createSelector(
  [getOriginalFormModel, getComponentProps],
  (formModel, { id, prop }) => {
    if (formModel[id] && prop === 'update') {
      return getControlModelWithAllUpdateRules(formModel[id]).update;
    } else {
      return formModel[id] && _.get(formModel, `${id}.${prop}`);
    }
  }
);

export const getGridColumnsChildren = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { childrenNodes }) =>
    childrenNodes
      .filter((node) => formModel[node].controlType === SS_CONTROL_TYPE.GRID_COLUMN)
      .map((node) => {
        const columnNodes = formModel[node].childrenNodes;
        return [...columnNodes];
      })
);

export const getAddedElementFormModelById = createSelector(
  [getAddedFormModelElements, getComponentProps],
  (elements, { controlId }) => elements && elements.find((item) => item.controlId === controlId)
);

export const getChildNodes = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { id }) => formModel[id]?.childrenNodes ?? []
);

export const hasControlDeltaChanges = createSelector(
  [getFormModel, getSessionFormModel, getComponentProps],
  (formModel, sessionFormModel, { id }) => {
    if (sessionFormModel && formModel[id] && sessionFormModel[id]) {
      const controlDiff = difference(formModel[id], sessionFormModel[id]);
      return !_.isEmpty(controlDiff);
    }
    return false;
  }
);

export const hasControlPropChanges = createSelector(
  [getModelById, getOriginalModelById, getComponentProps],
  (control, originalControl, { prop }) => {
    const propChanges = originalControl ? difference(control, originalControl) : {};

    return Object.keys(propChanges).includes(prop);
  }
);

export const hasControlPropChangesForUpdateRules = createSelector(
  [getModelById, getOriginalModelById, getComponentProps],
  (control, originalControl) => {
    const propChanges = originalControl ? difference(control, originalControl) : {};

    return (
      Object.keys(propChanges).includes('update') ||
      Object.keys(propChanges).includes('interaction')
    );
  }
);

export const getSaveDisabled = createSelector(
  [
    getFormModel,
    getOriginalFormModel,
    getDataSources,
    getOriginalDataSources,
    getDeletedFormModelElements,
  ],
  (formModel, originalFormModel, dataSources, originalDataSources, deletedElements) => {
    if (deletedElements?.length > 0) {
      return false;
    }

    if (Object.keys(dataSources).length !== Object.keys(originalDataSources).length) {
      return false;
    }

    const hasChanges = originalFormModel ? difference(formModel, originalFormModel) : {};
    if (Object.keys(hasChanges).length > 0) {
      return false;
    }

    const hasDataSourcesChanges = originalDataSources
      ? difference(dataSources, originalDataSources)
      : {};

    return Object.keys(hasDataSourcesChanges).length <= 0;
  }
);

export const getIsFormModelEmpty = createSelector(
  [getFormModel],
  (formModel) => !!Object.keys(formModel).length
);

export const getRules = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { id, prop }) => {
    const controlProperty = formModel[id] && _.get(formModel, `${id}.${prop}`);
    return preProcessActions(controlProperty?.rules ? controlProperty.rules : []);
  }
);

export const getUpdateRules = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { id }) => {
    const controlUpdateProperty = formModel[id] && _.get(formModel, `${id}.update`);
    const updateRules = preProcessActions(
      controlUpdateProperty?.rules ? controlUpdateProperty.rules : []
    );
    const controlInteractionProperty = formModel[id] && _.get(formModel, `${id}.interaction`);
    const interactionRules = preProcessActions(
      controlInteractionProperty?.rules ? controlInteractionProperty.rules : []
    );

    const modifiedInteractionRules = interactionRules
      .map((rule) => ({
        ...rule,
        dummyTarget: rule.parameterType === 'table' ? rule.dummyParameter : rule.parameter,
      }))
      .filter((rule) => INTERACTION_RULES_SUPPORTED_TYPES.includes(rule.type));

    return [...updateRules, ...modifiedInteractionRules];
  }
);

export const getIsSelectEntityDisabled = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { parentId }) => {
    const parentControl = formModel[parentId];
    if (!parentControl) {
      return false;
    }

    const { controlType = '', parent } = parentControl;

    switch (controlType) {
      case SS_CONTROL_TYPE.GRID_COLUMN:
      case SS_CONTROL_TYPE.FIELD_GROUP: {
        return true;
      }
      case SS_CONTROL_TYPE.ADVANCED_TEXT_BOX: {
        const { controlType: parentType } = formModel[parent];
        return parentType === SS_CONTROL_TYPE.GRID_COLUMN;
      }
      default:
        return false;
    }
  }
);

export const getIsAddButtonDisabled = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { id }) => {
    const control = formModel[id];

    if (
      control?.controlType === SS_CONTROL_TYPE.GRID &&
      ATTACHMENT_TYPES.includes(control?.gridType)
    ) {
      return true;
    }

    if (control?.controlType === SS_CONTROL_TYPE.GRID) {
      const { childrenNodes = [] } = control;
      const lastChildId = childrenNodes[childrenNodes.length - 1];
      const lastChild = lastChildId && formModel[lastChildId];

      return lastChild?.controlType === SS_CONTROL_TYPE.SUB_GRID;
    }

    return ![
      SS_CONTROL_TYPE.GRID_COLUMN,
      SS_CONTROL_TYPE.TEXT_BOX,
      SS_CONTROL_TYPE.SINGLE_TAB,
    ].includes(control?.controlType);
  }
);

export const getIsDeleteButtonDisabled = createSelector(
  [getFormModel, getComponentProps],
  (formModel, { id }) => {
    const control = formModel[id];
    const { controlType = '' } = control;

    if (![SS_CONTROL_TYPE.GRID_COLUMN, SS_CONTROL_TYPE.SINGLE_TAB].includes(controlType)) {
      return false;
    }

    const { childrenNodes = [] } = formModel[control?.parent];
    const gridColumns = childrenNodes.filter(
      (child) =>
        formModel[child]?.controlType === SS_CONTROL_TYPE.GRID_COLUMN ||
        formModel[child]?.controlType === SS_CONTROL_TYPE.SINGLE_TAB
    );
    return gridColumns.length === 1;
  }
);

export const getAllSubFormNames = createSelector([getFormModel], (formModel) =>
  Object.values(formModel).reduce(
    (acc, { controlType, formName }) =>
      controlType === SS_CONTROL_TYPE.SUB_FORM ? [...acc, formName] : acc,
    []
  )
);

export const getApiConfigurationVariables = createSelector(
  [getApiConfigurations, getComponentProps],
  (apiConfigurations, { configurationName, dataLookUpType, field }) => {
    if (dataLookUpType !== DATALOOKUP_TYPE_EXTERNAL_API || !configurationName) {
      return [];
    }

    return apiConfigurations[configurationName]?.[field] ?? [];
  }
);

export const getSsModalTargetIdOptions = createSelector([getFormModel], (formModel) => {
  const options = Object.values(formModel).reduce(
    (acc, { controlType, entityName = '', uniqueIdentifier }) => {
      const supportedControlTypes = [
        SS_CONTROL_TYPE.GRID,
        SS_CONTROL_TYPE.REPEATING_PANEL,
        SS_CONTROL_TYPE.PANEL,
      ];
      const isSecondLevelEntity = entityName && entityName.split('-').length === 2;

      return supportedControlTypes.includes(controlType) && isSecondLevelEntity && uniqueIdentifier
        ? [...acc, { label: uniqueIdentifier, value: uniqueIdentifier }]
        : acc;
    },
    []
  );

  return options.sort((a, b) => a.label.localeCompare(b.label));
});

export const getIsImportMode = createSelector([getEditor], (editor) => editor?.isImportMode);

export const getFlattedEntityDefinition = createSelector(
  [getEntityDefinition, getEntityTranslations],
  (entityDefinition, entityTranslations) =>
    getFlattenedEntityFields(entityDefinition, entityTranslations)
);

export const getInterActionOptions = createSelector(
  [getEntityDefinition, getFormModel, getTranslations],
  (entityDefinition, formModel, translations) => {
    const uniqueIdentifiers = getUniqueIdentifierData(formModel);

    const tables = {
      id: 'tables',
      label: 'Tables',
      children: entityDefinition ? getTableData(entityDefinition, translations) : {},
    };

    return uniqueIdentifiers.length
      ? [tables, { id: 'identifiers', label: 'Identifiers', children: uniqueIdentifiers }]
      : [tables];
  }
);
